import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  AudioFile as AudioIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { AdminJob, WorkflowState, CompletionStatus } from '../types';
import { adminApiService } from '../services/api';
import { Layout } from '../components/Layout';
import { useAuth } from '../auth/AuthProvider';
import { formatDateTime } from '../utils/dateTime';

const workflowStateColors: Record<WorkflowState, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  pending: 'default',
  started: 'info',
  submitted: 'success',
  processed: 'warning',
  completed: 'secondary',
};


const workflowStateLabels: Record<WorkflowState, string> = {
  pending: 'Pending',
  started: 'Started',
  submitted: 'Submitted',
  processed: 'Processed',
  completed: 'Completed',
};

const completionStatusLabels: Record<CompletionStatus, string> = {
  uploaded: 'Uploaded',
  upload_failed: 'Upload Failed',
  canceled: 'Canceled',
};

interface FileInfo {
  name: string;
  type: string;
  size?: number;
  storagePath: string;
  category: 'transcript' | 'exhibit' | 'audio' | 'order-form' | 'invoice' | 'worksheet-pdf' | 'submission-archive' | 'unknown';
  deponentName?: string;
  attorneyName?: string;
  isListed: boolean;
}

interface WorksheetData {
  jobNumber: string;
  confirmationId: string;
  submittedAt: string;
  formData: {
    // Worksheet payload schema version. Missing → treated as "1.0" (legacy
    // single invoiceFile). "1.1" adds invoiceFiles (array) alongside the legacy
    // singular. Future "2.0" will drop the singular entirely.
    schemaVersion?: string;
    // Basic job information
    jobNumber?: string;
    jobDate?: string;
    reporterName?: string;
    reporterEmail?: string;
    caseName?: string;
    jobType?: string;
    jobState?: string;
    isCancelled?: boolean;
    cancellationReason?: string;
    
    // Deponent information
    deponents?: Array<{
      id: string;
      name: string;
      exhibitStatus?: string;
      numPages?: number;
      readAndSign?: boolean;
      sendErrataTo?: string;
      specialProductionNotes?: string;
      startTimeHour?: string;
      startTimeMinute?: string;
      startTimeAmPm?: string;
      endTimeHour?: string;
      endTimeMinute?: string;
      endTimeAmPm?: string;
      transcriptType?: string;
      techExpert?: boolean;
      video?: boolean;
      interpreter?: boolean;
      afterHours?: boolean;
      weekendHoliday?: boolean;
      transcriptFiles?: Array<{ name: string; serverData?: { storagePath: string }; size?: number; type?: string }>;
      exhibitFiles?: Array<{ name: string; serverData?: { storagePath: string }; size?: number; type?: string }>;
      audioFiles?: Array<{ name: string; serverData?: { storagePath: string }; size?: number; type?: string }>;
    }>;
    
    // Attorney information
    attorneys?: Array<{
      id?: string;
      name: string;
      email?: string;
      firm?: string;
      mailingAddress?: string;
      telephonic?: boolean;
      transcriptOrders?: Array<{
        deponentId: string;
        orderType?: string;
        transcriptFormat?: string;
        deliveryType?: string;
        deliveryTimeline?: string;
        dueDate?: string;
        additionalServices?: {
          condensed?: boolean;
          word?: boolean;
          roughDraft?: boolean;
          exhibitLinks?: boolean;
          keywordIndex?: boolean;
        };
        orderSubmissionFiles?: Array<{ name: string; serverData?: { storagePath: string }; size?: number; type?: string }>;
      }>;
    }>;
    
    // Payment information
    attendanceType?: string;
    attendanceFee?: number;
    writingTime?: number;
    waitingTime?: number;
    payrollInfo?: string;
    specialNotes?: string;
    
    // Files
    invoiceFile?: { name: string; serverData?: { storagePath: string }; size?: number; type?: string };
    invoiceFiles?: Array<{ name: string; serverData?: { storagePath: string }; size?: number; type?: string }>;
    worksheetPdf?: { name: string; serverData?: { storagePath: string }; size?: number; type?: string };
  };
}

// Schema-version-aware selector for invoice file(s). "1.0" / missing → legacy
// single invoiceFile; "1.1"+ → invoiceFiles array.
const getInvoiceFilesFromWorksheet = (formData: WorksheetData['formData']): any[] => {
  const version = formData.schemaVersion ?? '1.0';
  if (version === '1.0') {
    return formData.invoiceFile ? [formData.invoiceFile] : [];
  }
  return Array.isArray(formData.invoiceFiles) ? formData.invoiceFiles : [];
};

const getFileIcon = (fileName: string, category: string) => {
  if (!fileName) return <FileIcon />;
  
  const extension = fileName.toLowerCase().split('.').pop() || '';
  
  if (category === 'worksheet-pdf' || extension === 'pdf') {
    return <PdfIcon color="error" />;
  }
  
  if (['mp3', 'wav', 'mp4', 'avi', 'mov'].includes(extension)) {
    return <AudioIcon color="primary" />;
  }
  
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) {
    return <ImageIcon color="success" />;
  }
  
  if (['mp4', 'avi', 'mov', 'wmv'].includes(extension)) {
    return <VideoIcon color="secondary" />;
  }
  
  return <FileIcon />;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};

const formatTime = (hour?: string, minute?: string, ampm?: string) => {
  if (!hour || !minute || !ampm) return '';
  return `${hour}:${minute} ${ampm}`;
};

const getOrderTypeDescription = (orderType?: string): string => {
  switch(orderType) {
    case 'Original': return 'Original';
    case 'Copy': return 'Copy';
    case 'FREE Copy': return 'FREE Copy';
    case 'Copy Estimate Only': return 'Copy Estimate Only';
    case 'Read & Sign Only': return 'Read & Sign Only';
    case 'No Order': return 'No Order';
    default: return orderType || 'Unknown';
  }
};

export const JobDetails: React.FC = () => {
  const { jobNumber } = useParams<{ jobNumber: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, accessToken } = useAuth();
  
  const [job, setJob] = useState<AdminJob | null>(null);
  const [worksheetData, setWorksheetData] = useState<WorksheetData | null>(null);
  const [allFiles, setAllFiles] = useState<FileInfo[]>([]);
  const [diagnosticFiles, setDiagnosticFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadWorksheetAndFiles = useCallback(async () => {
    if (!jobNumber) return;

    try {
      // Use the admin API service which includes authentication headers
      const filesResponse = await adminApiService.getJobFiles(jobNumber);
      
      if (filesResponse.success) {
        const allJobFiles = filesResponse.files || [];
        const worksheetFiles = filesResponse.worksheetFiles || [];

        if (worksheetFiles.length > 0) {
          // Get the most recent worksheet JSON file
          const latestWorksheet = worksheetFiles.sort((a: any, b: any) => 
            new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
          )[0];

          let worksheetData: WorksheetData | null = null;
          let listedFiles: FileInfo[] = [];

          // Fetch the worksheet JSON content
          try {
            worksheetData = await adminApiService.getJobWorksheet(jobNumber, latestWorksheet.name);
            setWorksheetData(worksheetData);
            if (worksheetData) {
              listedFiles = parseFilesFromWorksheet(worksheetData);
            }
          } catch (error) {
            console.error('Error loading worksheet JSON:', error);
          }
          
          // Identify diagnostic files (files in job directory not listed in worksheet)
          const listedPaths = new Set<string>();

          // Build comprehensive set of path variations for matching
          listedFiles.forEach(f => {
            const storagePath = f.storagePath;

            // Add the full storage path
            listedPaths.add(storagePath);

            // Extract filename from path (last segment)
            const fileName = storagePath.split('/').pop();
            if (fileName) {
              listedPaths.add(fileName);

              // Also try without timestamp prefix (e.g., "1764701297180_filename.pdf" -> "filename.pdf")
              const withoutTimestamp = fileName.replace(/^\d+_/, '');
              if (withoutTimestamp !== fileName) {
                listedPaths.add(withoutTimestamp);
              }
            }

            // Add variations with/without job number prefix
            listedPaths.add(`${jobNumber}/${storagePath}`);
            if (fileName) {
              listedPaths.add(`${jobNumber}/${fileName}`);
            }

            // Handle paths that might have different segment counts
            // e.g., "1425068/worksheetPdf/file.pdf" vs "worksheetPdf/file.pdf"
            const pathSegments = storagePath.split('/');
            if (pathSegments.length > 1) {
              // Try path without first segment
              listedPaths.add(pathSegments.slice(1).join('/'));
              // Try path without last segment (directory only)
              listedPaths.add(pathSegments.slice(0, -1).join('/') + '/');
            }
          });

          // Process all job files to categorize them
          const processedJobFiles = allJobFiles.map((file: any) => {
            // Check multiple variations of the file name/path
            const fileName = file.name;
            const fileNameWithoutTimestamp = fileName.replace(/^\d+_/, '');

            // Check if this file is already listed in the worksheet (check multiple variations)
            if (
              listedPaths.has(fileName) ||
              listedPaths.has(`${jobNumber}/${fileName}`) ||
              listedPaths.has(fileNameWithoutTimestamp) ||
              listedPaths.has(`${jobNumber}/${fileNameWithoutTimestamp}`)
            ) {
              return null; // Skip files already in the worksheet
            }
            
            // Special handling for worksheet PDFs - pattern matching
            if (file.name.includes('worksheet_') && file.name.includes(jobNumber) && file.name.endsWith('.pdf')) {
              return {
                name: file.name,
                type: file.contentType || 'application/pdf',
                size: file.size,
                storagePath: file.name,
                category: 'worksheet-pdf' as const,
                isListed: false
              };
            }
            
            // Special handling for submission archive ZIP files
            if (file.name.startsWith(`submission_${jobNumber}_`) && file.name.endsWith('.zip')) {
              return {
                name: file.name,
                type: file.contentType || 'application/zip',
                size: file.size,
                storagePath: file.name,
                category: 'submission-archive' as const,
                isListed: false
              };
            }
            
            // All other unrecognized files
            return {
              name: file.name,
              type: file.contentType || 'application/octet-stream',
              size: file.size,
              storagePath: file.name,
              category: 'unknown' as const,
              isListed: false
            };
          }).filter(Boolean) as FileInfo[];

          // Separate different file types found via pattern matching from diagnostic files
          const patternMatchedWorksheetPdfs = processedJobFiles.filter(f => f.category === 'worksheet-pdf');
          const patternMatchedSubmissionArchives = processedJobFiles.filter(f => f.category === 'submission-archive');
          const diagnosticFiles = processedJobFiles.filter(f => f.category === 'unknown');

          setAllFiles([...listedFiles, ...patternMatchedWorksheetPdfs, ...patternMatchedSubmissionArchives, ...diagnosticFiles]);
          setDiagnosticFiles(diagnosticFiles);
        } else {
          // No worksheet JSON files found, show all job files as diagnostic
          const unlisted = allJobFiles.map((file: any) => ({
            name: file.name,
            type: file.contentType || 'application/octet-stream',
            size: file.size,
            storagePath: file.name,
            category: 'unknown' as const,
            isListed: false
          }));

          setAllFiles(unlisted);
          setDiagnosticFiles(unlisted);
        }
      }
    } catch (err) {
      console.error('Error loading worksheet and files:', err);
    }
  }, [jobNumber]);

  const loadJobDetails = useCallback(async () => {
    if (!jobNumber) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load job details
      const jobResponse = await adminApiService.getJob(jobNumber);
      if (jobResponse.success && jobResponse.data) {
        setJob(jobResponse.data);
      }

      // Load worksheet data and files
      await loadWorksheetAndFiles();
      
    } catch (err) {
      console.error('Error loading job details:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobNumber, loadWorksheetAndFiles]);

  const parseFilesFromWorksheet = (data: WorksheetData): FileInfo[] => {
    const files: FileInfo[] = [];

    // Helper function to extract file metadata from both old and new structures
    const getFileMetadata = (file: any) => {
      // New structure: storagePath directly on file object
      if (file.storagePath) {
        return {
          storagePath: file.storagePath,
          name: file.filename || file.originalName || file.name,
          type: file.mimetype || file.type,
          size: file.size
        };
      }
      // Old structure: storagePath in serverData wrapper
      if (file.serverData?.storagePath) {
        return {
          storagePath: file.serverData.storagePath,
          name: file.originalName || file.serverData.originalName || file.serverData.filename,
          type: file.serverData.mimetype || file.type,
          size: file.serverData.size || file.size
        };
      }
      return null;
    };

    // Add worksheet PDF at the top
    const worksheetPdfMeta = getFileMetadata(data.formData.worksheetPdf);
    if (worksheetPdfMeta) {
      files.push({
        name: worksheetPdfMeta.name,
        type: worksheetPdfMeta.type || 'application/pdf',
        size: worksheetPdfMeta.size,
        storagePath: worksheetPdfMeta.storagePath,
        category: 'worksheet-pdf',
        isListed: true
      });
    }

    // Parse deponent files
    data.formData.deponents?.forEach(deponent => {
      // Transcript files
      deponent.transcriptFiles?.forEach(file => {
        const fileMeta = getFileMetadata(file);
        if (fileMeta) {
          files.push({
            name: fileMeta.name,
            type: fileMeta.type || 'text/plain',
            size: fileMeta.size,
            storagePath: fileMeta.storagePath,
            category: 'transcript',
            deponentName: deponent.name,
            isListed: true
          });
        }
      });

      // Exhibit files
      deponent.exhibitFiles?.forEach(file => {
        const fileMeta = getFileMetadata(file);
        if (fileMeta) {
          files.push({
            name: fileMeta.name,
            type: fileMeta.type || 'application/octet-stream',
            size: fileMeta.size,
            storagePath: fileMeta.storagePath,
            category: 'exhibit',
            deponentName: deponent.name,
            isListed: true
          });
        }
      });

      // Audio files
      deponent.audioFiles?.forEach(file => {
        const fileMeta = getFileMetadata(file);
        if (fileMeta) {
          files.push({
            name: fileMeta.name,
            type: fileMeta.type || 'audio/mpeg',
            size: fileMeta.size,
            storagePath: fileMeta.storagePath,
            category: 'audio',
            deponentName: deponent.name,
            isListed: true
          });
        }
      });
    });

    // Parse attorney order files
    data.formData.attorneys?.forEach(attorney => {
      attorney.transcriptOrders?.forEach(order => {
        order.orderSubmissionFiles?.forEach(file => {
          const fileMeta = getFileMetadata(file);
          if (fileMeta) {
            const deponentName = data.formData.deponents?.find(d => d.id === order.deponentId)?.name;
            files.push({
              name: fileMeta.name,
              type: fileMeta.type || 'application/pdf',
              size: fileMeta.size,
              storagePath: fileMeta.storagePath,
              category: 'order-form',
              deponentName,
              attorneyName: attorney.name,
              isListed: true
            });
          }
        });
      });
    });

    // Invoice file(s) — schema-version aware: 1.1 iterates invoiceFiles,
    // 1.0 falls back to the legacy singular invoiceFile.
    for (const invoiceEntry of getInvoiceFilesFromWorksheet(data.formData)) {
      const invoiceFileMeta = getFileMetadata(invoiceEntry);
      if (invoiceFileMeta) {
        files.push({
          name: invoiceFileMeta.name || 'invoice.pdf',
          type: invoiceFileMeta.type || 'application/pdf',
          size: invoiceFileMeta.size,
          storagePath: invoiceFileMeta.storagePath,
          category: 'invoice',
          isListed: true
        });
      }
    }

    return files;
  };

  useEffect(() => {
    // Only load job details if authenticated and have a token
    if (isAuthenticated && accessToken && jobNumber) {
      loadJobDetails();
    }
  }, [isAuthenticated, accessToken, jobNumber, loadJobDetails]);

  const handleDownloadFile = async (file: FileInfo) => {
    try {
      // Use the same base URL as other API calls
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const downloadUrl = `${apiBaseUrl}/admin/jobs/${jobNumber}/files/download?path=${encodeURIComponent(file.storagePath)}`;
      
      // Get the auth token
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }

      // Fetch the file with authentication headers
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const renderFilesList = (files: FileInfo[], title: string, showEmptyMessage = true) => {
    if (files.length === 0 && showEmptyMessage) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2 }}>
          No {title.toLowerCase()} found
        </Typography>
      );
    }

    const maxHeight = files.length > 4 ? 300 : 'auto';

    return (
      <List 
        dense 
        sx={{ 
          maxHeight,
          overflow: 'auto',
          border: files.length > 4 ? '1px solid #e0e0e0' : 'none',
          borderRadius: 1
        }}
      >
        {files.map((file, index) => (
          <ListItem key={index} divider>
            <ListItemIcon>
              {getFileIcon(file.name, file.category)}
            </ListItemIcon>
            <ListItemText
              primary={file.name}
              secondary={
                <Box>
                  {file.deponentName && (
                    <Typography variant="caption" display="block">
                      Deponent: {file.deponentName}
                    </Typography>
                  )}
                  {file.attorneyName && (
                    <Typography variant="caption" display="block">
                      Attorney: {file.attorneyName}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {file.type} {file.size && `• ${formatFileSize(file.size)}`}
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={() => handleDownloadFile(file)}
                title="Download file"
              >
                <DownloadIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  const pageActions = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/jobs')}
      variant="outlined"
    >
      Back to Jobs
    </Button>
  );

  if (loading) {
    return (
      <Layout pageTitle="Job Details" pageActions={pageActions}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !job) {
    return (
      <Layout pageTitle="Job Details" pageActions={pageActions}>
        <Alert severity="error">
          {error || 'Job not found'}
        </Alert>
      </Layout>
    );
  }

  const worksheetFiles = allFiles.filter(f => f.category === 'worksheet-pdf');
  const transcriptFiles = allFiles.filter(f => f.category === 'transcript');
  const exhibitFiles = allFiles.filter(f => f.category === 'exhibit');
  const audioFiles = allFiles.filter(f => f.category === 'audio');
  const orderFiles = allFiles.filter(f => f.category === 'order-form');
  const invoiceFiles = allFiles.filter(f => f.category === 'invoice');
  const submissionArchiveFiles = allFiles.filter(f => f.category === 'submission-archive');

  return (
    <Layout pageTitle={`Job ${jobNumber}`} pageActions={pageActions}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/jobs')}
          sx={{ textDecoration: 'none' }}
        >
          Jobs
        </Link>
        <Typography color="text.primary">Job {jobNumber}</Typography>
      </Breadcrumbs>

      <Grid container spacing={3}>
        {/* Job Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h5">
                  Job Information
                </Typography>
                {worksheetData && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Submitted: {formatDateTime(worksheetData.submittedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Confirmation: {worksheetData.confirmationId}
                    </Typography>
                    
                    {/* Worksheet PDF and Archive Files */}
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {worksheetFiles.length > 0 && worksheetFiles.map((file, index) => (
                        <Button
                          key={index}
                          size="small"
                          variant="outlined"
                          startIcon={<PdfIcon />}
                          onClick={() => handleDownloadFile(file)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Worksheet PDF
                        </Button>
                      ))}
                      {submissionArchiveFiles.length > 0 && submissionArchiveFiles.map((file, index) => (
                        <Button
                          key={index}
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadFile(file)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Archive ZIP
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              
              {/* Show cancellation alert if job was cancelled */}
              {worksheetData?.formData?.isCancelled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Job Cancelled</Typography>
                  {worksheetData.formData.cancellationReason && (
                    <Typography variant="body2">Reason: {worksheetData.formData.cancellationReason}</Typography>
                  )}
                </Alert>
              )}
              
              {/* Show expedited alert if any orders are expedited */}
              {worksheetData?.formData?.attorneys?.some(attorney => 
                attorney.transcriptOrders?.some(order => order.deliveryType === 'Expedited')
              ) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>EXPEDITED ORDERS</Typography>
                  <Typography variant="body2">This job contains expedited transcript orders that require priority processing.</Typography>
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Job Date</Typography>
                  <Typography>{new Date(job.jobDate + 'T12:00:00').toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Workflow State</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {job.workflowState === 'completed' && job.completionStatus ? (
                      <Chip
                        label={completionStatusLabels[job.completionStatus]}
                        size="small"
                        sx={{
                          backgroundColor: job.completionStatus === 'uploaded' ? '#9c27b0' : 
                                         job.completionStatus === 'upload_failed' ? '#f44336' : 
                                         job.completionStatus === 'canceled' ? '#757575' : '#9c27b0',
                          color: 'white',
                          border: `2px solid ${
                            job.completionStatus === 'canceled' ? '#757575' : 
                            job.completionStatus === 'upload_failed' ? '#f44336' : 
                            '#9c27b0'
                          }`,
                          fontWeight: job.completionStatus !== 'uploaded' ? 'bold' : 'normal',
                          '&:hover': {
                            backgroundColor: job.completionStatus === 'uploaded' ? '#7b1fa2' : 
                                           job.completionStatus === 'upload_failed' ? '#d32f2f' : 
                                           job.completionStatus === 'canceled' ? '#616161' : '#7b1fa2',
                          }
                        }}
                      />
                    ) : job.workflowState === 'completed' ? (
                      <Chip
                        label={workflowStateLabels[job.workflowState]}
                        size="small"
                        sx={{
                          backgroundColor: '#9c27b0',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#7b1fa2',
                          }
                        }}
                      />
                    ) : (
                      <Chip
                        label={workflowStateLabels[job.workflowState]}
                        color={workflowStateColors[job.workflowState]}
                        size="small"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Reporter</Typography>
                  <Typography>{worksheetData?.formData?.reporterName || job.reporterName || 'Not assigned'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Last Submitted</Typography>
                  <Typography>
                    {job.lastSubmitted
                      ? formatDateTime(job.lastSubmitted)
                      : 'Never submitted'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Case Name</Typography>
                  <Typography>{worksheetData?.formData?.caseName || job.caseName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Job Type</Typography>
                  <Typography>{worksheetData?.formData?.jobType || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">State</Typography>
                  <Typography>{worksheetData?.formData?.jobState || 'Not specified'}</Typography>
                </Grid>
                {worksheetData?.formData?.reporterEmail && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Reporter Email</Typography>
                    <Typography>{worksheetData.formData.reporterEmail}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Deponents Section - Only show if worksheet data is available */}
        {worksheetData?.formData?.deponents && worksheetData.formData.deponents.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Deponents ({worksheetData.formData.deponents.length})
                </Typography>
                {worksheetData.formData.deponents.map((deponent, index) => (
                  <Box key={deponent.id} sx={{ mb: 3, pb: 2, borderBottom: index < worksheetData.formData.deponents!.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none' }}>
                    <Typography variant="h6" gutterBottom>
                      {deponent.name}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {deponent.exhibitStatus && (
                            <ListItem>
                              <ListItemText 
                                primary={<Typography><strong>Exhibit Status:</strong> {deponent.exhibitStatus}</Typography>} 
                              />
                            </ListItem>
                          )}
                          {deponent.numPages && deponent.numPages > 0 && (
                            <ListItem>
                              <ListItemText 
                                primary={<Typography><strong>Number of Pages:</strong> {deponent.numPages}</Typography>} 
                              />
                            </ListItem>
                          )}
                          <ListItem>
                            <ListItemText 
                              primary={
                                <Typography>
                                  <strong>Read & Sign:</strong> {
                                    deponent.readAndSign === undefined ? 'Not specified' : 
                                    deponent.readAndSign ? 'Yes' : 'No'
                                  }
                                  {deponent.readAndSign === true && deponent.sendErrataTo && ` - Send To: ${deponent.sendErrataTo}`}
                                </Typography>
                              } 
                            />
                          </ListItem>
                          {deponent.specialProductionNotes && (
                            <ListItem>
                              <ListItemText 
                                primary={<Typography><strong>Special Production Notes:</strong> {deponent.specialProductionNotes}</Typography>} 
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {deponent.startTimeHour && deponent.startTimeMinute && deponent.startTimeAmPm && (
                            <ListItem>
                              <ListItemText 
                                primary={
                                  <Typography>
                                    <strong>Start Time:</strong> {formatTime(deponent.startTimeHour, deponent.startTimeMinute, deponent.startTimeAmPm)}
                                  </Typography>
                                } 
                              />
                            </ListItem>
                          )}
                          {deponent.endTimeHour && deponent.endTimeMinute && deponent.endTimeAmPm && (
                            <ListItem>
                              <ListItemText 
                                primary={
                                  <Typography>
                                    <strong>End Time:</strong> {formatTime(deponent.endTimeHour, deponent.endTimeMinute, deponent.endTimeAmPm)}
                                  </Typography>
                                } 
                              />
                            </ListItem>
                          )}
                          <ListItem>
                            <ListItemText 
                              primary={
                                <Typography>
                                  <strong>Transcript Type:</strong> {deponent.transcriptType === 'standard' ? 'Standard' : 
                                    deponent.transcriptType === 'realLegal' ? 'RealLegal' : 
                                    deponent.transcriptType === 'noTranscribe' ? 'No Transcribe' : 'Standard'}
                                </Typography>
                              } 
                            />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                    
                    {/* Additional Services */}
                    {(deponent.techExpert || deponent.video || deponent.interpreter || deponent.afterHours || deponent.weekendHoliday) && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Additional Services:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {deponent.techExpert && <Chip label="Tech/Expert" size="small" color="secondary" />}
                          {deponent.video && <Chip label="Video" size="small" color="secondary" />}
                          {deponent.interpreter && <Chip label="Interpreter" size="small" color="secondary" />}
                          {deponent.afterHours && <Chip label="After-hours" size="small" color="secondary" />}
                          {deponent.weekendHoliday && <Chip label="Weekend/Holiday" size="small" color="secondary" />}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Deponent Files */}
                    {(() => {
                      const deponentTranscriptFiles = transcriptFiles.filter(f => f.deponentName === deponent.name);
                      const deponentExhibitFiles = exhibitFiles.filter(f => f.deponentName === deponent.name);
                      const deponentAudioFiles = audioFiles.filter(f => f.deponentName === deponent.name);
                      const hasFiles = deponentTranscriptFiles.length > 0 || deponentExhibitFiles.length > 0 || deponentAudioFiles.length > 0;
                      
                      return hasFiles ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Files:</Typography>
                          {deponentTranscriptFiles.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Transcript Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {deponentTranscriptFiles.map((file, fileIndex) => (
                                  <Button
                                    key={fileIndex}
                                    size="small"
                                    variant="outlined"
                                    startIcon={getFileIcon(file.name, file.category)}
                                    onClick={() => handleDownloadFile(file)}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
                                  >
                                    {file.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {deponentExhibitFiles.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Exhibit Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {deponentExhibitFiles.map((file, fileIndex) => (
                                  <Button
                                    key={fileIndex}
                                    size="small"
                                    variant="outlined"
                                    startIcon={getFileIcon(file.name, file.category)}
                                    onClick={() => handleDownloadFile(file)}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
                                  >
                                    {file.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {deponentAudioFiles.length > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Audio Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {deponentAudioFiles.map((file, fileIndex) => (
                                  <Button
                                    key={fileIndex}
                                    size="small"
                                    variant="outlined"
                                    startIcon={getFileIcon(file.name, file.category)}
                                    onClick={() => handleDownloadFile(file)}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
                                  >
                                    {file.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Attorneys Section - Only show if worksheet data is available */}
        {worksheetData?.formData?.attorneys && worksheetData.formData.attorneys.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Attorneys ({worksheetData.formData.attorneys.length})
                </Typography>
                {worksheetData.formData.attorneys.map((attorney, index) => {
                  const getDeponentName = (deponentId: string): string => {
                    const deponent = worksheetData.formData.deponents?.find(d => d.id === deponentId);
                    return deponent ? deponent.name : 'Unknown Deponent';
                  };
                  
                  return (
                    <Box key={attorney.id || index} sx={{ mb: 3, pb: 2, borderBottom: index < worksheetData.formData.attorneys!.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none' }}>
                      <Typography variant="h6" gutterBottom>
                        {attorney.name}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <List dense>
                            {attorney.email && (
                              <ListItem>
                                <ListItemText 
                                  primary={<Typography><strong>Email:</strong> {attorney.email}</Typography>} 
                                />
                              </ListItem>
                            )}
                            {attorney.firm && (
                              <ListItem>
                                <ListItemText 
                                  primary={<Typography><strong>Firm:</strong> {attorney.firm}</Typography>} 
                                />
                              </ListItem>
                            )}
                            {attorney.mailingAddress && (
                              <ListItem>
                                <ListItemText 
                                  primary={<strong>Mailing Address:</strong>}
                                  secondary={attorney.mailingAddress}
                                />
                              </ListItem>
                            )}
                            {attorney.telephonic && (
                              <ListItem>
                                <ListItemText 
                                  primary={<Typography><strong>Attendance:</strong> Telephonic</Typography>} 
                                />
                              </ListItem>
                            )}
                          </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Transcript Orders:</Typography>
                          {attorney.transcriptOrders && attorney.transcriptOrders.length > 0 ? (
                            <List dense>
                              {attorney.transcriptOrders.map((order, orderIndex) => (
                                <ListItem key={orderIndex}>
                                  <ListItemText 
                                    primary={
                                      <Box>
                                        <Typography>
                                          <strong>{getDeponentName(order.deponentId)}:</strong> {getOrderTypeDescription(order.orderType)}
                                          {order.transcriptFormat ? ` (${order.transcriptFormat})` : ''}
                                        </Typography>
                                        {order.deliveryType === 'Expedited' && (
                                          <Box 
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              bgcolor: 'error.light',
                                              color: 'error.contrastText',
                                              borderRadius: '4px',
                                              px: 1,
                                              py: 0.5,
                                              mt: 0.5,
                                              width: 'fit-content'
                                            }}
                                          >
                                            <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography 
                                              variant="subtitle2"
                                              sx={{ 
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                lineHeight: 1
                                              }}
                                            >
                                              EXPEDITED{order.deliveryTimeline ? ` - ${order.deliveryTimeline}` : ''}
                                              {order.dueDate ? ` (Due: ${new Date(order.dueDate + 'T12:00:00').toLocaleDateString()})` : ''}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    } 
                                    secondary={
                                      <Box>
                                        {/* Additional Services */}
                                        {order.additionalServices && Object.values(order.additionalServices).some(val => val) && (
                                          <Box sx={{ mt: 0.5 }}>
                                            {Object.entries(order.additionalServices)
                                              .filter(([_, value]) => value)
                                              .map(([key]) => (
                                                <Chip 
                                                  key={key} 
                                                  label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')} 
                                                  size="small" 
                                                  sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                              ))
                                            }
                                          </Box>
                                        )}
                                        {/* Order Files */}
                                        {(() => {
                                          const orderFilesForThisOrder = orderFiles.filter(f => 
                                            f.attorneyName === attorney.name && f.deponentName === getDeponentName(order.deponentId)
                                          );
                                          return orderFilesForThisOrder.length > 0 ? (
                                            <Box sx={{ mt: 0.5 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>Order Files:</Typography>
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {orderFilesForThisOrder.map((file, fileIndex) => (
                                                  <Button
                                                    key={fileIndex}
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={getFileIcon(file.name, file.category)}
                                                    onClick={() => handleDownloadFile(file)}
                                                    sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
                                                  >
                                                    {file.name}
                                                  </Button>
                                                ))}
                                              </Box>
                                            </Box>
                                          ) : null;
                                        })()}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2">No transcript orders.</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Payment Information Section - Only show if worksheet data is available */}
        {worksheetData?.formData && (worksheetData.formData.attendanceType || worksheetData.formData.attendanceFee ||
          worksheetData.formData.writingTime || worksheetData.formData.waitingTime ||
          worksheetData.formData.payrollInfo || worksheetData.formData.specialNotes ||
          getInvoiceFilesFromWorksheet(worksheetData.formData).length > 0) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Payment Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      {worksheetData.formData.attendanceType && (
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Attendance Type:</strong> {worksheetData.formData.attendanceType}</Typography>} 
                          />
                        </ListItem>
                      )}
                      {worksheetData.formData.attendanceFee !== undefined && (
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Attendance Fee:</strong> ${worksheetData.formData.attendanceFee}</Typography>} 
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      {worksheetData.formData.writingTime !== undefined && worksheetData.formData.writingTime > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Writing Time:</strong> {worksheetData.formData.writingTime} hour(s)</Typography>} 
                          />
                        </ListItem>
                      )}
                      {worksheetData.formData.waitingTime !== undefined && worksheetData.formData.waitingTime > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Waiting Time:</strong> {worksheetData.formData.waitingTime} hour(s)</Typography>} 
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                </Grid>
                
                {/* Payroll Details */}
                {worksheetData.formData.payrollInfo && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Payroll Details:</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        {worksheetData.formData.payrollInfo}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {/* Special Notes */}
                {worksheetData.formData.specialNotes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Special Notes:</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        {worksheetData.formData.specialNotes}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {/* Invoice Files */}
                {invoiceFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Invoice Files:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {invoiceFiles.map((file, index) => (
                        <Button
                          key={index}
                          size="small"
                          variant="outlined"
                          startIcon={getFileIcon(file.name, file.category)}
                          onClick={() => handleDownloadFile(file)}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {file.name}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Additional Files Section - Only show if there are unmatched/diagnostic files */}
        {diagnosticFiles.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="h5">
                    Additional Files Found ({diagnosticFiles.length})
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These files were found in the job directory but were not listed in the submitted worksheet. 
                  This may indicate files uploaded separately or processing artifacts.
                </Alert>
                {renderFilesList(diagnosticFiles, 'Additional Files', false)}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Layout>
  );
};